import React, { useEffect, useState } from 'react';
import { ApiLink, Product } from '../../types';
import { useCrudList } from '../../hooks/useCrudList';
import { MessageSetter } from '../../utils/messages';
import { apiRequest } from '../../utils/api';
import ProductSelector from './ProductSelector';

interface LinksProps {
  setMessage: MessageSetter;
}

const LinksSection: React.FC<LinksProps> = ({ setMessage }) => {
  const [allProducts, setAllProducts] = useState<Product[]>([]);

  // Fetch all products for settings (not filtered by global selection)
  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const data = await apiRequest<Product[]>('/api/products', {}, { useAuth: true });
        setAllProducts(data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setAllProducts([]);
      }
    };
    fetchAllProducts();
  }, []);

  const {
    items: apiLinks,
    loading: loadingApiLinks,
    fetchAll: fetchApiLinks,
    create: createLink,
    update: updateLink,
    remove: removeLink,
  } = useCrudList<ApiLink>({
    endpoint: '/api/apilinks',
    useAuth: true,
    setMessage,
  });

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [editingLink, setEditingLink] = useState<string | null>(null);
  const [linkForm, setLinkForm] = useState<{
    title: string;
    url: string;
    linkType: ApiLink['linkType'];
    productIds: string[];
  }>({
    title: '',
    url: '',
    linkType: 'other',
    productIds: [],
  });

  useEffect(() => {
    fetchApiLinks();
  }, [fetchApiLinks]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (showLinkModal) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showLinkModal]);

  // In settings, always show all links regardless of global product selection
  // Settings should not be affected by the global product filter
  const filteredLinks = apiLinks;

  // Helper functions to display products
  const getProductInitials = (product: Product): string => {
    if (product.projectId) {
      return product.projectId.toUpperCase();
    }
    const name = product.name.trim();
    const words = name.split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    if (name.length >= 2) {
      return (name[0] + name[1]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getProductColor = (name: string): string => {
    const colors = [
      '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981',
      '#6366F1', '#EF4444', '#14B8A6', '#F97316',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const handleEditLink = (link: ApiLink) => {
    setEditingLink(link.id);
    setLinkForm({
      title: link.title,
      url: link.url,
      linkType: link.linkType,
      productIds: link.productIds || [],
    });
    setShowLinkModal(true);
  };

  const handleOpenNewLink = () => {
    setEditingLink('new');
    setLinkForm({ title: '', url: '', linkType: 'other', productIds: [] });
    setShowLinkModal(true);
  };

  const resetForm = () => {
    setEditingLink(null);
    setLinkForm({ title: '', url: '', linkType: 'other', productIds: [] });
    setShowLinkModal(false);
  };

  const handleSaveLink = async () => {
    if (!linkForm.title.trim() || !linkForm.url.trim()) {
      setMessage({ type: 'error', text: 'Title and URL are required' });
      return;
    }

    try {
      const idToSave = editingLink && editingLink !== 'new' ? editingLink : undefined;
      if (idToSave) {
        await updateLink(idToSave, linkForm, 'Link updated successfully');
        // Refresh after update to ensure we have the latest data
        await fetchApiLinks();
      } else {
        // Create the link - useCrudList will optimistically update the list
        const newLink = await createLink(linkForm, 'Link created successfully');
        // Refresh to ensure we have the latest data from server (including workspaceId)
        // This is important because the optimistic update might not have all fields
        await fetchApiLinks();
        // Double-check: if the new link isn't in the list after fetch, add it
        // This handles race conditions where the server hasn't fully committed
        setTimeout(async () => {
          const currentLinks = await apiRequest<ApiLink[]>('/api/apilinks', {}, { useAuth: true });
          const linkExists = currentLinks?.some(l => l.id === newLink.id);
          if (!linkExists) {
            // If link still doesn't exist, refresh again
            await fetchApiLinks();
          }
        }, 500);
      }
      resetForm();
    } catch (error) {
      console.error('Error saving link:', error);
      setMessage({ type: 'error', text: 'Failed to save link. Please try again.' });
      // Refresh on error to ensure consistency
      await fetchApiLinks();
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    if (!window.confirm('Are you sure you want to delete this link?')) {
      return;
    }

    try {
      await removeLink(linkId, 'Link deleted successfully');
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
          Links Configuration
        </h2>
        {!showLinkModal && (
          <button
            className="btn btn-primary"
            onClick={handleOpenNewLink}
          >
            ➕ Add Link
          </button>
        )}
      </div>
      <p
        style={{
          marginBottom: '1.5rem',
          color: 'var(--text-secondary)',
          fontSize: '0.875rem',
        }}
      >
        Configure external URLs for bug tracking, test case creation, test case issue tracking,
        and other services. These links will be used throughout the application instead of
        hardcoded values.
      </p>

      {loadingApiLinks ? (
        <div>Loading...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Links List */}
          {filteredLinks.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔗</div>
              <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                No links configured
              </h3>
              <p style={{ color: 'var(--text-muted)' }}>
                Add links for GitHub, issue tracking, test case management, and other services.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {filteredLinks.map((link) => (
                <div
                  key={link.id}
                  className="card"
                  style={{
                    padding: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        marginBottom: '0.25rem',
                      }}
                    >
                      {link.title}
                    </div>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: 'var(--text-secondary)',
                        marginBottom: '0.25rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      {link.url}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                      Type:{' '}
                      <span style={{ textTransform: 'capitalize' }}>
                        {link.linkType.replace('-', ' ')}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      {link.productIds && link.productIds.length > 0 ? (
                        link.productIds.map(productId => {
                          const product = allProducts.find(p => p.id === productId);
                          if (!product) return null;
                          const initials = getProductInitials(product);
                          const color = getProductColor(product.name);
                          return (
                            <div
                              key={productId}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                padding: '0.25rem 0.5rem',
                                backgroundColor: color,
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                color: '#fff',
                                fontWeight: 500,
                              }}
                              title={product.name}
                            >
                              <span>{initials}</span>
                              <span>{product.name}</span>
                            </div>
                          );
                        })
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                          All products
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleEditLink(link)}
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleDeleteLink(link.id)}
                      style={{
                        fontSize: '0.875rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Link Edit Modal */}
      {showLinkModal && (
        <div
          className="modal-overlay"
          onClick={resetForm}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem',
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              backgroundColor: 'var(--surface)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--grid-line)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
                {editingLink === 'new' ? 'Add New Link' : 'Edit Link'}
              </h2>
              <button
                className="btn btn-ghost btn-icon"
                onClick={resetForm}
                style={{ fontSize: '1.5rem', padding: '0.5rem' }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Title *
                </label>
                <input
                  type="text"
                  value={linkForm.title}
                  onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
                  placeholder="e.g., Hive GitHub Issues"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  URL *
                </label>
                <input
                  type="url"
                  value={linkForm.url}
                  onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
                  placeholder="https://github.com/org/repo/issues/new"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Link Type *
                </label>
                <select
                  value={linkForm.linkType}
                  onChange={(e) =>
                    setLinkForm({
                      ...linkForm,
                      linkType: e.target.value as ApiLink['linkType'],
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '0.5rem 2.5rem 0.5rem 0.75rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    appearance: 'none',
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2300d4ff' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E\")",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                  }}
                >
                  <option value="bug">Bug Links</option>
                  <option value="test-case">Test Case Creation</option>
                  <option value="test-case-issue">Test Case Issue Tracking</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Products</label>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                  Select products for this link. Leave all unselected to use for all products.
                </p>
                <ProductSelector
                  selectedProductIds={linkForm.productIds}
                  onChange={(productIds) => {
                    setLinkForm({
                      ...linkForm,
                      productIds,
                    });
                  }}
                  products={allProducts}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    await handleSaveLink();
                  }}
                >
                  Save
                </button>
                <button className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinksSection;
